import { createSelector } from '@ngrx/store';

import * as fromAppState from '../states/app.state';
import * as fromStaticDataState from '../states/static-data.state';
import { SpeciesDiagnosisDropdown } from 'src/app/medical-history/models/species-diagnosis-dropdown.model';
import { BookType } from 'src/app/calendar/shared/models/book-type.enum';
import { ConfigurationType } from 'src/app/settings/enums/configuration-type.enum';
import { Department } from 'src/app/api/models';

export const {
  selectIds,
  selectEntities,
  selectAll,
  selectTotal
} = fromStaticDataState.adapter.getSelectors(fromAppState.getSectionStaticData);

export const getReasons = createSelector(
  fromAppState.getSectionStaticData,
  staticData => staticData.reasons
);
export const getUserConfig = createSelector(
  fromAppState.getSectionStaticData,
  staticData => staticData.userConfig
);
export const getDepartments = createSelector(
  fromAppState.getSectionStaticData,
  staticData => staticData.departments
);
export const getFirms = createSelector(
  fromAppState.getSectionStaticData,
  staticData => staticData.firms
);
export const getAllFirms = createSelector(
  fromAppState.getSectionStaticData,
  staticData => staticData.firms
);
export const isLoadedMedicalHistory = createSelector(
  fromAppState.getSectionStaticData,
  staticData => staticData.isloadedMedicalHistory
);
export const getMedicalHistory = createSelector(
  fromAppState.getSectionStaticData,
  (state) => ({
    departments: state.departments,
    firms: state.firms,
    inventoryList: state.inventoryList,
    mhInitialization: state.mhInitialization
  }));
export const isLoadedHexData = createSelector(
  fromAppState.getSectionStaticData,
  staticData => staticData.isLoadedHexData
);
export const isLoadedServiceArticleTreatment = createSelector(
  fromAppState.getSectionStaticData,
  staticData => staticData.isLoadedServiceArticleTreatment
);
export const isLoadedSpecisDiagnosis = createSelector(
  fromAppState.getSectionStaticData,
  staticData => staticData.isLoadedSpeciesDiagnosis
);
export const getSpeciesDiagnoses = () => createSelector(
  fromAppState.getSectionStaticData,
  staticData => {
    let speciesDiagnosisList: SpeciesDiagnosisDropdown[] = [];
    // Filtering by bovine (codeLevel1: 2, codeLevel2: 1 ), add other animals here
    const speciesUsed = staticData.speciesDiagnoses?.species?.filter(species => (species.codeLevel1 === 2 && species.codeLevel2 === 1));
    // For getting only the species ids into an array
    const specieIdsUsed = speciesUsed ? speciesUsed.map(species => species.id) : [];
    const diagnosisList = staticData.speciesDiagnoses?.diagnoses?.filter(diagnosis => (!diagnosis.codeLevel3 && !diagnosis.codeLevel4));

    if (diagnosisList && diagnosisList.length > 0 && specieIdsUsed) {
      // Based on the species ids found, gets all their diagnoses with codeLevel1 and codeLevel2
      staticData.speciesDiagnoses?.speciesToDiagnoses?.forEach(speciesDiagnosis => {
        const diagnosis = diagnosisList.find(data => data.id === speciesDiagnosis.diagnosisId && specieIdsUsed.includes(speciesDiagnosis.speciesId));
        if (diagnosis && speciesUsed) {
          const speciesData = speciesUsed.find(species => species.id === speciesDiagnosis.speciesId);
          const speciesCode = speciesData?.codeLevel1 + '.' + speciesData?.codeLevel2;
          const diagnosisCode =
            (diagnosis.codeLevel1 ? diagnosis.codeLevel1 : '') +
            (diagnosis.codeLevel1 && diagnosis.codeLevel2 ? '.' + diagnosis.codeLevel2 : '');

          // Create a new object with updated designationDe, designationFr, and designationIt
          const updatedSpeciesDiagnosis = {
            ...speciesDiagnosis,
            designationDe: speciesDiagnosis.designationDe ? diagnosisCode + ' ' + speciesDiagnosis.designationDe : speciesDiagnosis.designationDe,
            designationFr: speciesDiagnosis.designationFr ? diagnosisCode + ' ' + speciesDiagnosis.designationFr : speciesDiagnosis.designationFr,
            designationIt: speciesDiagnosis.designationIt ? diagnosisCode + ' ' + speciesDiagnosis.designationIt : speciesDiagnosis.designationIt
          };

          speciesDiagnosisList.push({ ...updatedSpeciesDiagnosis, speciesCode, ...diagnosis });
        }
      });
    }

    if (speciesDiagnosisList && speciesDiagnosisList.length > 0) {
      speciesDiagnosisList = speciesDiagnosisList.sort((a, b) => {
        if (a.designationDe && b?.designationDe) {
          const diagnosisNumberA = Number(a.designationDe.split(' ')[0]);
          const diagnosisNumberB = Number(b.designationDe.split(' ')[0]);
          return diagnosisNumberA - diagnosisNumberB;
        }
        return -1;
      });
    }

    return speciesDiagnosisList;
  }
);
export const getHexSpecies = () => createSelector(
  fromAppState.getSectionStaticData,
  staticData => staticData.speciesDiagnoses?.species
);
export const getServiceArticleTreatments = createSelector(
  fromAppState.getSectionStaticData,
  staticData => staticData.serviceArticleTreatments
);
export const getServiceArticleTreatmentsAll = createSelector(
  fromAppState.getSectionStaticData,
  staticData => staticData.serviceArticleTreatments
);
export const getServiceArticleTreatmentsByArticleId = (articleId: string | undefined) => createSelector(
  fromAppState.getSectionStaticData,
  staticData => staticData.serviceArticleTreatments.filter(serviceArticleTreatment => serviceArticleTreatment.salesArticle?.articleId === articleId)
);
export const getInventoryList = createSelector(
  fromAppState.getSectionStaticData,
  staticData => staticData.inventoryList
);
export const getLocations = createSelector(
  fromAppState.getSectionStaticData,
  staticData => staticData.locations
);
export const getLocationById = (locationId: string) => {
  return createSelector(
    fromAppState.getSectionStaticData,
    staticData => staticData.locations.find(state => state.id === locationId)
  );
}
export const getLocationByAddressId = (addressId: string) => {
  return createSelector(
    fromAppState.getSectionStaticData,
    staticData => staticData.locations.find(state => state.addressId === addressId)
  );
}
export const getMhInitialization = createSelector(
  fromAppState.getSectionStaticData,
  staticData => staticData.mhInitialization
);
export const getApplicationTypeHexData = createSelector(
  fromAppState.getSectionStaticData,
  staticData => staticData.applicationTypes
);
export const getServingModesHexData = createSelector(
  fromAppState.getSectionStaticData,
  staticData => staticData.servingModes
);

export const getServingModesHexDataEn = createSelector(
  fromAppState.getSectionStaticData,
  staticData => staticData.servingModes ? staticData.servingModes.filter(servingMode => servingMode.category === 'EN').sort((a, b) => {
    if (a.name < b.name) {
      return -1;
    }
    if (a.name > b.name) {
      return 1;
    }
    return 0;
  }) : []
);

export const getPresenceTypes = createSelector(
  fromAppState.getSectionStaticData,
  staticData => staticData.presenceTypes
);

export const getAppointmentStatesByBookId = (bookId: string) => {
  return createSelector(
    fromAppState.getSectionStaticData,
    staticData => staticData.appointmentStates.filter(state => state.bookId === bookId)
  );
}
export const getTimeRangeConfigsByBookIdBookType = (bookId: string, bookType: BookType) => {
  return createSelector(
    fromAppState.getSectionStaticData,
    staticData => staticData.timeRangeConfigs.filter(state => state.bookId === bookId && state.bookType === bookType)
  );
}

export const getVersion = () => {
  return createSelector(
    fromAppState.getSectionStaticData,
    staticData => {
      return staticData.version;
    }
  );
}
export const getIsNotificationEnabled = () => {
  return createSelector(
    fromAppState.getSectionStaticData,
    staticData => {
      return staticData.userConfig.isNotificationEnabled;
    }
  );


}

export const getAnimalTypes = () => {
  return createSelector(
    fromAppState.getSectionStaticData,
    staticData => {
      return staticData.animalTypes;
    }
  );

}
export const isLoadedAnimalTypes = createSelector(
  fromAppState.getSectionStaticData,
  staticData => staticData.isLoadedAnimalTypes
);

export const getCountryList = () => {
  return createSelector(
    fromAppState.getSectionStaticData,
    staticData => {
      return staticData.countryList;
    }
  );

}
export const isCountryListLoaded = createSelector(
  fromAppState.getSectionStaticData,
  staticData => staticData.isCountryListLoaded
);

export const getCountyList = () => {
  return createSelector(
    fromAppState.getSectionStaticData,
    staticData => {
      return staticData.countyList;
    }
  );

}
export const isCountyListLoaded = createSelector(
  fromAppState.getSectionStaticData,
  staticData => staticData.isCountyListLoaded
);

export const getBookSettingList = () => {
  return createSelector(
    fromAppState.getSectionStaticData,
    staticData => {
      return staticData.bookSettingList;
    }
  );
}

export const isBookSettingListLoaded = createSelector(
  fromAppState.getSectionStaticData,
  staticData => staticData.isBookSettingListLoaded
);

export const getAvailableBooksSettingsToCopy = (bookId: string) => {
  return createSelector(
    fromAppState.getSectionStaticData,
    staticData => {
      return staticData.bookSettingList.filter(book => book.bookId !== bookId).filter(book => book.bookType === BookType.Consultation);
    }
  );
}



export const getLocationAddressIds = (entityId: string, typeEntity: ConfigurationType) => {
  return createSelector(
    fromAppState.getSectionStaticData,
    staticData => {
      let ids: { locationAddressId: string | undefined, addressId: string | undefined } = {
        locationAddressId: undefined,
        addressId: undefined
      }
      switch (typeEntity) {
        case ConfigurationType.DEPARTMENT: {
          staticData.firms.map((firm) => {
            if (firm.departments && firm.departments.length > 0) {
              const department = firm.departments.find(dep =>
                dep.departmentId === entityId
              );
              if (department) {
                ids.addressId = department.addressId;
                ids.locationAddressId = department.locationAddressId;
              }
            }
          });

          break
        }
        case ConfigurationType.BOOK: {
          const bookSettingList = staticData.bookSettingList.find(book =>
            book.bookId === entityId
          );
          if (bookSettingList) {
            ids.addressId = bookSettingList.addressId;
            ids.locationAddressId = bookSettingList.locationAddressId;
          }
          break
        }
        case ConfigurationType.FIRM: {
          const firms = staticData.firms.find(firm =>
            firm.firmId === entityId
          );
          if (firms) {
            ids.addressId = firms.addressId;
          }
          break
        }
      }
      return ids
    }
  );
}

export const getDepartmentById = (departmentId: string) => {
  return createSelector(
    fromAppState.getSectionStaticData,
    staticData => {
      let department: Department | undefined = undefined;
      staticData.firms.map((firm) => {
        if (firm.departments && firm.departments.length > 0) {
          firm.departments.map(dep => {
            if (dep.departmentId === departmentId) {
              department = dep;
            }
          }
          );
        }
      });
      return department;
    }
  );
}

export const getCustomerExternalNumbers = () => createSelector(
  fromAppState.getSectionStaticData,
  staticData => {
      return staticData.customerExternalNumbers;
    }
);

export const isCustomerExternalNumbersLoaded = createSelector(
  fromAppState.getSectionStaticData,
  staticData => staticData.isCustomerExternalNumbersLoaded
);
export const getAnimalExternalNumbers = () => createSelector(
  fromAppState.getSectionStaticData,
  staticData => {
      return staticData.animalExternalNumbers;
    }
);

export const isAnimalExternalNumbersLoaded = createSelector(
  fromAppState.getSectionStaticData,
  staticData => staticData.isAnimalExternalNumbersLoaded
);
export const getAnimalDateTypes = () => createSelector(
  fromAppState.getSectionStaticData,
  staticData => {
      return staticData.animalDateTypes;
    }
);

export const isAnimalDateTypesLoaded = createSelector(
  fromAppState.getSectionStaticData,
  staticData => staticData.isAnimalDateTypesLoaded
);
export const getCustomerCategoryGroup = () => createSelector(
  fromAppState.getSectionStaticData,
  staticData => {
      return staticData.customerCategoryGroup;
    }
);

export const isCustomerCategoryGroupLoaded = createSelector(
  fromAppState.getSectionStaticData,
  staticData => staticData.isCustomerCategoryGroupLoaded
);
export const getCustomerCategory = () => createSelector(
  fromAppState.getSectionStaticData,
  staticData => {
      return staticData.customerCategory;
    }
);

export const isCustomerCategoryLoaded = createSelector(
  fromAppState.getSectionStaticData,
  staticData => staticData.isCustomerCategoryLoaded
);

export const translations = createSelector(fromAppState.getSectionStaticData, (sd) => sd.translations);
export const translationsByKeys = (props: { keys: string[] }) =>
  createSelector(
    translations,
    data => {
      return data.filter(translation => props.keys.includes(translation.key));
    }
  );
